/**
 * Stable, versioned body sampling set for pit zones. It deliberately does not
 * use a renderer sprite's bounds: browser and backend replay sample these
 * exact points from the resolved post-collision state.
 */
export function getVehicleZoneContactPoints(state, config) {
    const geometry = getVehicleGeometry(state, config);
    const halfLength = config.bodyLength / 2;
    const { forwardX, forwardY, rightX, rightY } = basis(state.headingRad);
    const point = (longitudinal, lateral) => ({
        x: state.position.x + forwardX * longitudinal + rightX * lateral,
        y: state.position.y + forwardY * longitudinal + rightY * lateral,
    });
    return [
        ...geometry.corners,
        ...geometry.sideMidpoints,
        point(halfLength, 0),
        point(-halfLength, 0),
        ...geometry.wheels.map(({ x, y }) => ({ x, y })),
    ];
}
export function getVehicleZoneOccupancy(state, config, sampler) {
    let pitServiceActive = false;
    let pitSpeedLimitActive = false;
    for (const point of getVehicleZoneContactPoints(state, config)) {
        const zones = sampler.sample(point.x, point.y).zones ?? 0;
        pitServiceActive ||= (zones & 1) !== 0;
        pitSpeedLimitActive ||= (zones & 2) !== 0;
        if (pitServiceActive && pitSpeedLimitActive)
            break;
    }
    return { pitServiceActive, pitSpeedLimitActive };
}
function basis(headingRad) {
    return {
        forwardX: Math.sin(headingRad),
        forwardY: -Math.cos(headingRad),
        rightX: Math.cos(headingRad),
        rightY: Math.sin(headingRad),
    };
}
export function getVehicleGeometry(state, config) {
    const { forwardX, forwardY, rightX, rightY } = basis(state.headingRad);
    const halfWidth = config.trackWidth / 2;
    const halfLength = config.bodyLength / 2;
    const points = (longitudinal, lateral) => ({
        x: state.position.x + forwardX * longitudinal + rightX * lateral,
        y: state.position.y + forwardY * longitudinal + rightY * lateral,
    });
    return {
        wheels: [
            {
                wheel: "frontLeft",
                axle: "front",
                side: "left",
                ...points(config.comToFrontAxle, -halfWidth),
            },
            {
                wheel: "frontRight",
                axle: "front",
                side: "right",
                ...points(config.comToFrontAxle, halfWidth),
            },
            {
                wheel: "rearLeft",
                axle: "rear",
                side: "left",
                ...points(-config.comToRearAxle, -halfWidth),
            },
            {
                wheel: "rearRight",
                axle: "rear",
                side: "right",
                ...points(-config.comToRearAxle, halfWidth),
            },
        ],
        corners: [
            points(halfLength, -halfWidth),
            points(halfLength, halfWidth),
            points(-halfLength, -halfWidth),
            points(-halfLength, halfWidth),
        ],
        sideMidpoints: [points(0, -halfWidth), points(0, halfWidth)],
    };
}
/** Sample each named wheel independently for tire wear and force calculations. */
export function getWheelContacts(state, config, sampler) {
    return getVehicleGeometry(state, config).wheels.map((wheel) => ({
        wheel: wheel.wheel,
        worldPosition: { x: wheel.x, y: wheel.y },
        terrain: sampler.sample(wheel.x, wheel.y).terrain,
        normalLoadRatio: 0.25,
    }));
}
export function bodyBasis(headingRad) {
    return basis(headingRad);
}
